// Controles de formulario del design-system propio: Button (con estado isLoading), Input (con
// label/error opcionales, compatible con register de react-hook-form vía spread) y Select
// controlado (value/onChange(value)/options). Construidos sobre los primitivos shadcn/Radix de
// src/components/ui/*. Input y Select asocian el <label> con su control (htmlFor/id, id generado
// con useId si no se pasa) para accesibilidad y para que getByLabelText los encuentre.
import {
    forwardRef,
    useId,
    useState,
    type ButtonHTMLAttributes,
    type InputHTMLAttributes,
    type ReactNode,
} from 'react';
import type { VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button as UiButton, buttonVariants } from '@/components/ui/button';
import { Input as UiInput } from '@/components/ui/input';
import {
    Select as UiSelect,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];
type ButtonSize = VariantProps<typeof buttonVariants>['size'];

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    fullWidth?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant,
            size,
            isLoading = false,
            fullWidth = false,
            leftIcon,
            rightIcon,
            disabled,
            className,
            children,
            ...props
        },
        ref,
    ) => (
        <UiButton
            ref={ref}
            variant={variant}
            size={size}
            disabled={disabled || isLoading}
            aria-busy={isLoading}
            className={cn(fullWidth && 'w-full', className)}
            {...props}
        >
            {isLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : leftIcon}
            {children}
            {rightIcon}
        </UiButton>
    ),
);
Button.displayName = 'Button';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    /** Reemplaza la tipografía del label, p. ej. para igualar las etiquetas de su renglón. */
    labelClassName?: string;
    error?: string;
    hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, labelClassName, error, hint, className, id, required, ...props }, ref) => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const errorId = `${inputId}-error`;
        const describedBy =
            [props['aria-describedby'], error ? errorId : undefined].filter(Boolean).join(' ') ||
            undefined;
        const field = (
            <UiInput
                ref={ref}
                id={inputId}
                required={required}
                aria-invalid={error ? true : undefined}
                className={cn(
                    error && 'border-destructive focus-visible:border-destructive',
                    className,
                )}
                {...props}
                aria-describedby={describedBy}
            />
        );
        if (!label && !error && !hint) return field;
        return (
            <div className="space-y-1.5">
                {label ? (
                    <label
                        htmlFor={inputId}
                        className={labelClassName ?? 'text-sm font-medium text-foreground'}
                    >
                        {label}
                        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
                    </label>
                ) : null}
                {field}
                {error ? (
                    <p id={errorId} className="text-xs text-destructive">
                        {error}
                    </p>
                ) : hint ? (
                    <p className="text-xs text-muted-foreground">{hint}</p>
                ) : null}
            </div>
        );
    },
);
Input.displayName = 'Input';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
    /** Nota a la derecha de la opción en la lista; no se pinta en el control. */
    hint?: string;
}

export interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    label?: string;
    placeholder?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    id?: string;
    className?: string;
}

export function Select({
    value,
    onChange,
    options,
    label,
    placeholder,
    error,
    required,
    disabled,
    id,
    className,
}: SelectProps) {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const field = (
        <UiSelect value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger
                id={selectId}
                aria-invalid={error ? true : undefined}
                className={cn(error && 'border-destructive', className)}
            >
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {options.map((opt) => (
                    <SelectItem
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.disabled}
                        hint={opt.hint}
                    >
                        {opt.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </UiSelect>
    );
    if (!label && !error) return field;
    return (
        <div className="space-y-1.5">
            {label ? (
                <label htmlFor={selectId} className="text-sm font-medium text-foreground">
                    {label}
                    {required ? <span className="ml-0.5 text-destructive">*</span> : null}
                </label>
            ) : null}
            {field}
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
    );
}

export interface NumberInputProps {
    value: number;
    onValueChange: (value: number) => void;
    id?: string;
    step?: number;
    min?: number;
    max?: number;
    disabled?: boolean;
    placeholder?: string;
    'aria-label'?: string;
    className?: string;
}

/**
 * Input numérico con borrador local: mientras tiene foco muestra lo tecleado tal cual (se puede
 * vaciar, o escribir "23" sin pelear con un 0 pegado); solo propaga números válidos, y al perder
 * el foco vuelve al valor canónico.
 */
export function NumberInput({
    value,
    onValueChange,
    id,
    step,
    min,
    max,
    disabled,
    placeholder,
    className,
    ...props
}: NumberInputProps) {
    const [draft, setDraft] = useState<string | null>(null);
    return (
        <input
            type="number"
            id={id}
            step={step}
            min={min}
            max={max}
            disabled={disabled}
            placeholder={placeholder}
            value={draft ?? String(value)}
            onChange={(e) => {
                const raw = e.target.value;
                setDraft(raw);
                const num = Number(raw);
                if (raw !== '' && Number.isFinite(num)) {
                    onValueChange(num);
                }
            }}
            onBlur={() => {
                setDraft(null);
            }}
            className={cn(
                'rounded border border-border bg-background px-2 py-[5px] text-xs tabular-nums outline-none focus:border-ring disabled:opacity-50',
                className,
            )}
            {...props}
        />
    );
}

export interface SwitchProps {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    'aria-label'?: string;
    className?: string;
}

/** Toggle on/off (`.toggle` del mock: pill 40×22, on = success). Controlado, con rol switch. */
export function Switch({ checked, onCheckedChange, disabled, className, ...props }: SwitchProps) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => {
                onCheckedChange(!checked);
            }}
            className={cn(
                'relative inline-flex h-[22px] w-10 flex-none items-center rounded-full transition-colors',
                checked ? 'bg-success' : 'bg-muted',
                disabled && 'cursor-not-allowed opacity-50',
                className,
            )}
            {...props}
        >
            <span
                aria-hidden
                className={cn(
                    'absolute left-0.5 size-[18px] rounded-full bg-card shadow transition-transform',
                    checked && 'translate-x-[18px]',
                )}
            />
        </button>
    );
}
